import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  SUMMER_SCHOOL_VIDEO_ID,
  getSummerSchoolBar2026Content,
} from "../../data/summerSchoolBar2026";
import openingSpeaker from "../../assets/img/summer-school-2026/opening-speaker.webp";
import openingGuests from "../../assets/img/summer-school-2026/opening-guests.webp";
import openingTeam from "../../assets/img/summer-school-2026/opening-team.webp";
import openingAudience from "../../assets/img/summer-school-2026/opening-audience.webp";
import cultureWide from "../../assets/img/summer-school-2026/culture-wide.webp";
import cultureDance from "../../assets/img/summer-school-2026/culture-dance.webp";
import cultureFpep from "../../assets/img/summer-school-2026/culture-fpep.webp";
import portOfBar from "../../assets/img/summer-school-2026/port-of-bar.webp";
import europeHouse from "../../assets/img/summer-school-2026/europe-house.webp";
import studentsEuropeHouse from "../../assets/img/summer-school-2026/students-europe-house.webp";
import "./SummerSchoolBar2026.css";

const galleryImages = [
  openingSpeaker,
  openingGuests,
  openingTeam,
  cultureWide,
  cultureDance,
  portOfBar,
  studentsEuropeHouse,
  europeHouse,
];

function SectionHeading({ eyebrow, title, text, align = "left", theme = "light" }) {
  return (
    <div
      className={`ssb2026-heading ssb2026-heading--${align} ssb2026-heading--${theme}`}
    >
      <span className="ssb2026-eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {text ? <p>{text}</p> : null}
    </div>
  );
}

function ExternalLink({ href, children, className = "" }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
    </a>
  );
}

function VideoModal({ content, onClose }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.classList.add("ssb2026-modal-open");
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("ssb2026-modal-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="ssb2026-modal"
      role="dialog"
      aria-modal="true"
      aria-label={content.modalTitle}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="ssb2026-modal__panel ssb2026-modal__panel--video">
        <button
          type="button"
          className="ssb2026-modal__close"
          onClick={onClose}
          aria-label={content.close}
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
        <div className="ssb2026-video-frame">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${SUMMER_SCHOOL_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
            title={content.modalTitle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}

function GalleryModal({ content, activeIndex, setActiveIndex, onClose }) {
  const imageCount = galleryImages.length;

  const goPrevious = useCallback(() => {
    setActiveIndex((current) => (current - 1 + imageCount) % imageCount);
  }, [imageCount, setActiveIndex]);

  const goNext = useCallback(() => {
    setActiveIndex((current) => (current + 1) % imageCount);
  }, [imageCount, setActiveIndex]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") goPrevious();
      if (event.key === "ArrowRight") goNext();
    };

    document.body.classList.add("ssb2026-modal-open");
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("ssb2026-modal-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [goNext, goPrevious, onClose]);

  return (
    <div
      className="ssb2026-modal"
      role="dialog"
      aria-modal="true"
      aria-label={content.title}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="ssb2026-modal__panel ssb2026-modal__panel--gallery">
        <button
          type="button"
          className="ssb2026-modal__close"
          onClick={onClose}
          aria-label={content.close}
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>

        <button
          type="button"
          className="ssb2026-gallery-nav ssb2026-gallery-nav--prev"
          onClick={goPrevious}
          aria-label={content.previous}
        >
          <i className="fas fa-chevron-left" aria-hidden="true" />
        </button>

        <figure className="ssb2026-lightbox-figure">
          <img
            src={galleryImages[activeIndex]}
            alt={content.captions[activeIndex]}
          />
          <figcaption>
            <span>{content.captions[activeIndex]}</span>
            <small>
              {activeIndex + 1} / {imageCount}
            </small>
          </figcaption>
        </figure>

        <button
          type="button"
          className="ssb2026-gallery-nav ssb2026-gallery-nav--next"
          onClick={goNext}
          aria-label={content.next}
        >
          <i className="fas fa-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function AgendaCard({ day, index }) {
  return (
    <article className="ssb2026-agenda-card">
      <div className="ssb2026-agenda-card__top">
        <div>
          <span className="ssb2026-agenda-card__number">0{index + 1}</span>
          <p>{day.date}</p>
          <h3>{day.label}</h3>
        </div>
        <div className="ssb2026-agenda-card__location">
          <i className="fas fa-map-marker-alt" aria-hidden="true" />
          <span>{day.location}</span>
        </div>
      </div>

      <p className="ssb2026-agenda-card__summary">{day.summary}</p>

      <div className="ssb2026-agenda-card__maps">
        <ExternalLink href={day.mapUrl}>
          <i className="fas fa-location-arrow" aria-hidden="true" />
          {day.mapLabel}
        </ExternalLink>
        {day.secondaryMapUrl ? (
          <ExternalLink href={day.secondaryMapUrl}>
            <i className="fas fa-location-arrow" aria-hidden="true" />
            {day.secondaryMapLabel}
          </ExternalLink>
        ) : null}
      </div>

      <ol className="ssb2026-timeline">
        {day.items.map((item) => (
          <li key={`${day.date}-${item.time}-${item.title}`}>
            <time>{item.time}</time>
            <div>
              <h4>{item.title}</h4>
              <p>{item.detail}</p>
            </div>
          </li>
        ))}
      </ol>
    </article>
  );
}

function SummerSchoolBar2026() {
  const { i18n } = useTranslation();
  const content = useMemo(
    () => getSummerSchoolBar2026Content(i18n.language),
    [i18n.language],
  );
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(null);

  const closeVideo = useCallback(() => setVideoOpen(false), []);
  const closeGallery = useCallback(() => setActiveGalleryIndex(null), []);

  return (
    <main className="ssb2026-page">
      <section className="ssb2026-hero" aria-labelledby="ssb2026-title">
        <div className="ssb2026-hero__glow ssb2026-hero__glow--one" />
        <div className="ssb2026-hero__glow ssb2026-hero__glow--two" />
        <div className="container ssb2026-hero__container">
          <div className="ssb2026-hero__copy">
            <nav className="ssb2026-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">{content.breadcrumbHome}</Link>
              <i className="fas fa-chevron-right" aria-hidden="true" />
              <span>{content.breadcrumbCurrent}</span>
            </nav>
            <span className="ssb2026-eyebrow">{content.hero.eyebrow}</span>
            <h1 id="ssb2026-title">{content.hero.title}</h1>
            <p className="ssb2026-hero__subtitle">{content.hero.subtitle}</p>
            <p className="ssb2026-hero__lead">{content.hero.lead}</p>
            <div className="ssb2026-hero__actions">
              <button
                type="button"
                className="ssb2026-button ssb2026-button--primary"
                onClick={() => setVideoOpen(true)}
              >
                <span className="ssb2026-button__icon">
                  <i className="fas fa-play" aria-hidden="true" />
                </span>
                {content.hero.primaryCta}
              </button>
              <a
                href="#program"
                className="ssb2026-button ssb2026-button--ghost"
              >
                {content.hero.secondaryCta}
                <i className="fas fa-arrow-down" aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="ssb2026-hero-collage" aria-hidden="true">
            <figure className="ssb2026-hero-collage__main">
              <img src={cultureWide} alt="" />
            </figure>
            <figure className="ssb2026-hero-collage__top">
              <img src={openingSpeaker} alt="" />
            </figure>
            <figure className="ssb2026-hero-collage__bottom">
              <img src={studentsEuropeHouse} alt="" />
            </figure>
            <div className="ssb2026-hero-collage__stamp">
              <strong>2026</strong>
              <span>BAR · MONTENEGRO</span>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="ssb2026-facts">
            {content.hero.facts.map((fact) => (
              <div key={fact.value} className="ssb2026-fact">
                {fact.type === "country-flags" ? (
                  <strong
                    className="ssb2026-fact__flags"
                    aria-label={fact.value}
                  >
                    <span
                      className="flag-icon flag-icon-at ssb2026-fact__flag"
                      aria-hidden="true"
                    />
                    <span
                      className="ssb2026-fact__flag-connector"
                      aria-hidden="true"
                    >
                      ×
                    </span>
                    <span
                      className="flag-icon flag-icon-me ssb2026-fact__flag"
                      aria-hidden="true"
                    />
                  </strong>
                ) : (
                  <strong>{fact.value}</strong>
                )}
                <span>{fact.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ssb2026-section ssb2026-intro">
        <div className="container">
          <div className="ssb2026-intro__grid">
            <div className="ssb2026-intro__content">
              <SectionHeading
                eyebrow={content.intro.eyebrow}
                title={content.intro.title}
              />
              {content.intro.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              <blockquote>
                <i className="fas fa-quote-left" aria-hidden="true" />
                <p>{content.intro.quote}</p>
              </blockquote>
            </div>
            <div className="ssb2026-intro__media">
              <img src={openingGuests} alt={content.gallery.captions[1]} />
              <div className="ssb2026-intro__media-note">
                <i className="fas fa-globe-europe" aria-hidden="true" />
                <span>FPEP BAR × HOCHSCHULE BURGENLAND</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ssb2026-section ssb2026-pillars">
        <div className="container">
          <SectionHeading
            eyebrow={content.pillars.eyebrow}
            title={content.pillars.title}
            align="center"
          />
          <div className="ssb2026-pillars__grid">
            {content.pillars.items.map((item, index) => (
              <article className="ssb2026-pillar" key={item.title}>
                <span className="ssb2026-pillar__index">0{index + 1}</span>
                <div className="ssb2026-pillar__icon">
                  <i className={`fas ${item.icon}`} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ssb2026-section ssb2026-video-section">
        <div className="container">
          <div className="ssb2026-video-card">
            <button
              type="button"
              className="ssb2026-video-card__media"
              onClick={() => setVideoOpen(true)}
              aria-label={content.video.button}
            >
              <img src={openingAudience} alt="" />
              <span className="ssb2026-video-card__shade" />
              <span className="ssb2026-video-card__play">
                <i className="fas fa-play" aria-hidden="true" />
              </span>
              <span className="ssb2026-video-card__label">
                YouTube · FPEP BAR
              </span>
            </button>
            <div className="ssb2026-video-card__copy">
              <SectionHeading
                eyebrow={content.video.eyebrow}
                title={content.video.title}
                text={content.video.text}
              />
              <button
                type="button"
                className="ssb2026-text-link"
                onClick={() => setVideoOpen(true)}
              >
                {content.video.button}
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="program" className="ssb2026-section ssb2026-agenda">
        <div className="container">
          <SectionHeading
            eyebrow={content.agenda.eyebrow}
            title={content.agenda.title}
            text={content.agenda.intro}
            align="center"
          />
          <div className="ssb2026-agenda__visuals">
            <figure>
              <img src={openingSpeaker} alt={content.gallery.captions[0]} />
              <figcaption>06 / JUL / 2026</figcaption>
            </figure>
            <figure>
              <img src={portOfBar} alt={content.gallery.captions[5]} />
              <figcaption>10 / JUL / 2026</figcaption>
            </figure>
          </div>
          <div className="ssb2026-agenda__grid">
            {content.agenda.days.map((day, index) => (
              <AgendaCard key={day.date} day={day} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="ssb2026-section ssb2026-culture">
        <div className="container">
          <div className="ssb2026-culture__grid">
            <div className="ssb2026-culture__media">
              <figure className="ssb2026-culture__wide">
                <img src={cultureWide} alt={content.gallery.captions[3]} />
              </figure>
              <figure className="ssb2026-culture__small">
                <img src={cultureDance} alt={content.gallery.captions[4]} />
              </figure>
              <figure className="ssb2026-culture__small ssb2026-culture__small--second">
                <img src={cultureFpep} alt={content.gallery.captions[3]} />
              </figure>
            </div>
            <div className="ssb2026-culture__copy">
              <SectionHeading
                eyebrow={content.culture.eyebrow}
                title={content.culture.title}
              />
              {content.culture.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              <div className="ssb2026-city-route">
                {content.culture.cities.map((city, index) => (
                  <React.Fragment key={city}>
                    <span>{city}</span>
                    {index < content.culture.cities.length - 1 ? (
                      <i className="fas fa-long-arrow-alt-right" aria-hidden="true" />
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
              <div className="ssb2026-statehood">
                <strong>{content.culture.statehoodLabel}</strong>
                <span>{content.culture.statehoodText}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ssb2026-section ssb2026-partners">
        <div className="container">
          <SectionHeading
            eyebrow={content.partners.eyebrow}
            title={content.partners.title}
            text={content.partners.intro}
            align="center"
          />
          <div className="ssb2026-partners__grid">
            {content.partners.items.map((partner, index) => (
              <article className="ssb2026-partner" key={partner.name}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{partner.name}</h3>
                  <p>{partner.role}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ssb2026-future">
        <div className="container">
          <div className="ssb2026-future__grid">
            <div>
              <SectionHeading
                eyebrow={content.future.eyebrow}
                title={content.future.title}
                theme="dark"
              />
            </div>
            <div className="ssb2026-future__copy">
              <p>{content.future.text}</p>
              <p>{content.future.euText}</p>
              <ExternalLink
                href={content.future.linkUrl}
                className="ssb2026-button ssb2026-button--light"
              >
                {content.future.linkLabel}
                <i className="fas fa-external-link-alt" aria-hidden="true" />
              </ExternalLink>
            </div>
          </div>
        </div>
      </section>

      <section className="ssb2026-section ssb2026-gallery">
        <div className="container">
          <SectionHeading
            eyebrow={content.gallery.eyebrow}
            title={content.gallery.title}
            align="center"
          />
          <div className="ssb2026-gallery__grid">
            {galleryImages.map((image, index) => (
              <button
                type="button"
                className={`ssb2026-gallery__item ssb2026-gallery__item--${index + 1}`}
                key={image}
                onClick={() => setActiveGalleryIndex(index)}
                aria-label={`${content.gallery.open}: ${content.gallery.captions[index]}`}
              >
                <img src={image} alt={content.gallery.captions[index]} />
                <span className="ssb2026-gallery__overlay">
                  <i className="fas fa-expand-alt" aria-hidden="true" />
                  <small>{String(index + 1).padStart(2, "0")}</small>
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="ssb2026-section ssb2026-cta">
        <div className="container">
          <div className="ssb2026-cta__card">
            <div className="ssb2026-cta__copy">
              <span className="ssb2026-eyebrow">{content.cta.eyebrow}</span>
              <h2>{content.cta.title}</h2>
              <p>{content.cta.text}</p>
            </div>
            <div className="ssb2026-cta__links">
              <Link to="/erasmus">
                <i className="fas fa-plane-departure" aria-hidden="true" />
                {content.cta.links.erasmus}
              </Link>
              <Link to="/erasmus-konkursi">
                <i className="fas fa-bullhorn" aria-hidden="true" />
                {content.cta.links.calls}
              </Link>
              <Link to="/novosti">
                <i className="far fa-newspaper" aria-hidden="true" />
                {content.cta.links.news}
              </Link>
              <Link to="/galerija">
                <i className="far fa-images" aria-hidden="true" />
                {content.cta.links.gallery}
              </Link>
              <ExternalLink href="https://www.instagram.com/fpepbar/">
                <i className="fab fa-instagram" aria-hidden="true" />
                {content.cta.links.social}
              </ExternalLink>
            </div>
          </div>
        </div>
      </section>

      {videoOpen ? <VideoModal content={content.video} onClose={closeVideo} /> : null}
      {activeGalleryIndex !== null ? (
        <GalleryModal
          content={content.gallery}
          activeIndex={activeGalleryIndex}
          setActiveIndex={setActiveGalleryIndex}
          onClose={closeGallery}
        />
      ) : null}
    </main>
  );
}

export default SummerSchoolBar2026;
